/**
 * useCountUp — animates a number from its previous value to `target`
 * (rAF, ease-out). Respects prefers-reduced-motion (jumps immediately).
 *
 * @param {number} target
 * @param {{ duration?: number }} options
 * @returns {number} displayed value (integer)
 */

import { useEffect, useRef, useState } from "react";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function useCountUp(target, { duration = 900 } = {}) {
  const safeTarget = Number.isFinite(Number(target)) ? Number(target) : 0;
  const [value, setValue] = useState(safeTarget);
  const fromRef = useRef(safeTarget);
  const frameRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) {
      fromRef.current = safeTarget;
      setValue(safeTarget);
      return undefined;
    }
    const from = fromRef.current;
    const delta = safeTarget - from;
    if (delta === 0) return undefined;
    const startTs = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startTs) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = from + delta * eased;
      setValue(Math.round(next));
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = safeTarget;
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      fromRef.current = safeTarget;
    };
  }, [safeTarget, duration]);

  return value;
}
